<template>
  <!-- Context change marker -->
  <div
    v-if="message.role === 'context'"
    class="flex items-center gap-3 py-1"
  >
    <div class="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
    <span class="flex-shrink-0 text-xs text-slate-400 dark:text-slate-500">
      {{ message.content }}
    </span>
    <div class="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
  </div>

  <!-- Regular message -->
  <div
    v-else
    class="flex gap-3"
    :class="[message.role === 'user' ? 'flex-row-reverse' : 'flex-row']"
  >
    <!-- Avatar (only for assistant) -->
    <div
      v-if="message.role === 'assistant'"
      class="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full bg-slate-200 p-0.5 dark:bg-slate-700"
    >
      <img
        src="/img/righty-light.jpg"
        alt="Righty"
        class="h-full w-full rounded-full object-cover dark:hidden"
      >
      <img
        src="/img/righty-dark.jpg"
        alt="Righty"
        class="hidden h-full w-full rounded-full object-cover dark:block"
      >
    </div>

    <!-- Message Content Column (allows changelog to stack below message) -->
    <div class="flex flex-col gap-3 max-w-[85%]">
      <!-- Message Bubble -->
      <div
        class="rounded-2xl px-4 py-3"
        :class="[
          message.role === 'user'
            ? 'bg-[#b4637a] text-white rounded-br-md'
            : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700'
        ]"
      >
      <!-- User message -->
      <div
        v-if="message.role === 'user'"
        class="whitespace-pre-wrap text-sm"
        v-html="renderedUserContent"
      />

      <!-- Assistant message - interleaved text, plans, and templates -->
      <template v-else>
        <template
          v-for="(segment, index) in messageSegments"
          :key="index"
        >
          <!-- Text segment -->
          <div
            v-if="segment.type === 'text' && segment.content.trim()"
            class="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-headings:my-3 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5"
            :class="{ 'mt-3': index > 0 }"
            v-html="renderMarkdown(segment.content)"
          />

          <!-- Inline tool call indicator (Ferndesk-style) -->
          <RightyInlineToolCall
            v-else-if="segment.type === 'tool' && segment.tool"
            :tool-name="segment.tool.toolName!"
            :status="segment.tool.toolStatus || 'completed'"
            :tool-arguments="segment.tool.toolArguments"
            :tool-error="segment.tool.toolError"
          />

          <!-- Persisted question prompt (already answered) -->
          <RightyQuestionPrompt
            v-else-if="segment.type === 'question'"
            :question="segment.question?.question"
            :options="segment.question?.options"
            :allow-custom="segment.question?.allowCustom"
            :selected-option="segment.question?.selectedOption"
            :is-disabled="true"
          />
        </template>

        <!-- Streaming indicator when no content yet -->
        <span
          v-if="isStreaming && !message.content"
          class="inline-flex items-center gap-1 text-sm text-slate-400"
        >
          <span
            class="h-2 w-2 animate-bounce rounded-full bg-indigo-400"
            style="animation-delay: 0ms"
          />
          <span
            class="h-2 w-2 animate-bounce rounded-full bg-indigo-400"
            style="animation-delay: 150ms"
          />
          <span
            class="h-2 w-2 animate-bounce rounded-full bg-indigo-400"
            style="animation-delay: 300ms"
          />
        </span>

        <!-- Streaming cursor -->
        <span
          v-else-if="isStreaming && messageSegments.length > 0 && messageSegments[messageSegments.length - 1].type === 'text'"
          class="hidden ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-indigo-500"
        />
      </template>
      </div>

      <!-- Changelog card (shown AFTER message bubble, not inside it) -->
      <RightyChangelogCard
        v-if="message.role === 'assistant' && effectiveChangelog.length > 0"
        :changelog="effectiveChangelog"
        :is-latest="isLatestChangelog"
        :is-undone="isUndone"
        @undo="handleUndo"
      />

      <!-- Thinking duration (shown for completed assistant messages) -->
      <div
        v-if="message.role === 'assistant' && message.metadata?.thinkingDuration && !isStreaming"
        class="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500"
      >
        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12,6 12,12 16,14" />
        </svg>
        <span>Thought for {{ message.metadata.thinkingDuration }}s</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { marked } from 'marked'
import type { RightyMessage, StreamSegment, ChangelogItem } from '../types'
import { useCitationLinks } from '../composables/useCitationLinks'
import RightyChangelogCard from './RightyChangelogCard.vue'
import RightyInlineToolCall from './RightyInlineToolCall.vue'
import RightyQuestionPrompt from './RightyQuestionPrompt.vue'

interface MessageSegment {
  type: 'text' | 'tool' | 'question'
  content: string
  /** Tool call data (for type === 'tool') */
  tool?: StreamSegment
  /** Question data (for type === 'question') */
  question?: StreamSegment
}

const props = defineProps<{
  message: RightyMessage
  isStreaming?: boolean
  /** Changelog for this specific message (from backend-applied plan) */
  changelog?: ChangelogItem[]
  /** Whether this message has the most recent changelog in the thread */
  isLatestChangelog?: boolean
  /** Whether this message's changelog has been undone */
  isUndone?: boolean
}>()

const emit = defineEmits<{
  undo: [messageId: number]
}>()

const { transformCitationLinks } = useCitationLinks()

// Effective changelog: prefer frontend-set changelog (from prop), fallback to backend metadata
const effectiveChangelog = computed<ChangelogItem[]>(() => {
  if (props.changelog && props.changelog.length > 0) return props.changelog
  return (props.message.metadata?.planApplied?.changelog ?? []) as ChangelogItem[]
})

// Parse message into interleaved segments of text and tool calls
const messageSegments = computed<MessageSegment[]>(() => {
  if (!props.message.content || props.message.role !== 'assistant') return []

  const content = props.message.content
  const segments: MessageSegment[] = []

  // If this message has interleaved segments from streaming (single-phase agentic mode),
  // use them directly to preserve the text <-> tool interleaving order.
  const storedSegments = props.message.metadata?.segments as StreamSegment[] | undefined
  if (storedSegments && storedSegments.length > 0 && storedSegments.some((s) => s.type === 'tool' || s.type === 'question')) {
    for (const seg of storedSegments) {
      if (seg.type === 'text' && seg.content?.trim()) {
        segments.push({ type: 'text', content: seg.content })
      } else if (seg.type === 'tool' && seg.toolName) {
        segments.push({ type: 'tool', content: '', tool: seg })
      } else if (seg.type === 'question') {
        segments.push({ type: 'question', content: '', question: seg })
      }
    }
    return segments
  }

  // Otherwise, treat the entire content as a single text segment
  if (content.trim()) {
    segments.push({ type: 'text', content })
  }

  return segments
})

// Render user content - convert citation links to clickable chips
const renderedUserContent = computed(() => {
  if (!props.message.content || props.message.role !== 'user') return ''

  // Escape HTML first to prevent XSS from user-pasted content
  let content = props.message.content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

  // Transform [Name](/path) to styled chips (applied after escaping, so our HTML is safe)
  content = content.replace(
    /\[([^\]]+)\]\(\/([^)]+)\)/g,
    (_, text, _path) => {
      return `<span class="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-white">${text}</span>`
    }
  )

  return content
})

// Render markdown text with citation link support
function renderMarkdown(content: string): string {
  if (!content) return ''

  // Remove action blocks from display (legacy format)
  let processedContent = content.replace(/```action[\s\S]*?```/g, '')

  // Style "Plan:" lines as a distinct visual element
  processedContent = processedContent.replace(
    /^Plan:\s*(.+)$/m,
    '<span class="righty-plan-line">$1</span>'
  )

  // Convert internal links to citation-style references with data attributes for Vue Router
  processedContent = transformCitationLinks(processedContent)

  // Parse markdown with custom renderer
  const renderer = new marked.Renderer()

  // Custom link renderer for external links
  renderer.link = ({ href, title, text }: { href: string; title: string | null; text: string }) => {
    const titleAttr = title ? ` title="${title}"` : ''

    // Convert mailto links to internal visitor links (except for RightMessage support emails)
    if (href && href.startsWith('mailto:')) {
      const email = href.replace('mailto:', '')

      // Keep mailto: links for RightMessage support emails
      if (email.endsWith('@rightmessage.com')) {
        return `<a href="${href}"${titleAttr} class="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400">${text}</a>`
      }

      const encodedEmail = encodeURIComponent(email)
      return `<span class="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-indigo-600 dark:bg-slate-700 dark:text-indigo-400">${text}</span>`
    }

    const isExternal = href && href.startsWith('http')
    if (isExternal) {
      return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400">${text}</a>`
    }
    return `<a href="${href || '#'}"${titleAttr} class="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400">${text}</a>`
  }

  return marked.parse(processedContent, {
    breaks: true,
    gfm: true,
    renderer
  }) as string
}


function handleUndo() {
  emit('undo', props.message.id)
}

</script>

<style scoped>
.prose :deep(a) {
  @apply text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300;
}

.prose :deep(code) {
  @apply rounded bg-slate-200 px-1 py-0.5 text-sm dark:bg-slate-600;
}

.prose :deep(pre) {
  @apply rounded-lg bg-slate-900 p-3 text-sm text-slate-100;
}

.prose :deep(pre code) {
  @apply bg-transparent p-0;
}

.prose :deep(ul),
.prose :deep(ol) {
  @apply my-2 pl-5;
}

.prose :deep(li) {
  @apply my-1;
}

.prose :deep(h1),
.prose :deep(h2),
.prose :deep(h3),
.prose :deep(h4) {
  @apply font-semibold text-slate-800 dark:text-slate-100;
}

.prose :deep(h1) {
  @apply text-lg;
}

.prose :deep(h2) {
  @apply text-base;
}

.prose :deep(h3),
.prose :deep(h4) {
  @apply text-sm;
}

.prose :deep(blockquote) {
  @apply border-l-4 border-indigo-300 pl-3 italic text-slate-600 dark:border-indigo-600 dark:text-slate-400;
}

.prose :deep(table) {
  @apply w-full text-sm;
}

.prose :deep(th) {
  @apply border-b border-slate-300 pb-2 text-left font-semibold dark:border-slate-600;
}

.prose :deep(td) {
  @apply border-b border-slate-200 py-2 dark:border-slate-700;
}

.prose :deep(strong) {
  @apply font-semibold text-slate-800 dark:text-slate-100;
}

.prose :deep(em) {
  @apply italic;
}

:deep(.righty-plan-line) {
  display: block;
  font-size: 0.8rem;
  font-style: italic;
  opacity: 0.5;
  margin-top: 0.25rem;
}

/* Animation for bouncing dots */
@keyframes bounce {
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
}
</style>
